/**
 * Combines the employee-side net-salary engine (`calculate-finnish-net-salary.ts`),
 * employer-side contributions (`calculate-employer-cost.ts`), and an income-linked VAT
 * estimate (`estimate-vat-burden.ts`) into a single "tax iceberg" result: the full stack
 * of tax between what an employer actually pays to employ someone and what that person
 * can actually still spend once VAT is paid back out on their net income.
 *
 * This is the calculation core for the /tax-iceberg page. It does not add any new tax
 * logic of its own — see the three modules above for sourcing and methodology.
 */

import { calculateNetSalary, type NetSalaryInput, type NetSalaryResult } from './calculate-finnish-net-salary';
import { calculateEmployerCost, type EmployerCostResult, type EmployerSize } from './calculate-employer-cost';
import { estimateVatBurden, type VatBurdenEstimate } from './estimate-vat-burden';
import { calculateCapitalIncome, type CapitalIncomeResult } from './calculate-capital-income';

export interface TaxIcebergInput {
  grossAnnual: number;
  municipalTaxRatePct?: number;
  churchTaxRatePct?: number;
  age?: number;
  dependentChildren?: number;
  isSoleGuardian?: boolean;
  employerSize?: EmployerSize;
  /** Trade union / unemployment fund fees, EUR/year (deductible from earned income). */
  unionFees?: number;
  /** Commuting costs, EUR/year (deductible above the own-liability share, capped). */
  commutingCosts?: number;
  /** Dividends from listed companies, EUR/year, gross. Taxed as capital income, no employer cost. */
  dividends?: number;
  /** Capital gains net of acquisition cost, EUR/year. Taxed as capital income, no employer cost. */
  capitalGains?: number;
  /** Dividends from an unlisted company the person owns, EUR/year, and the mathematical value of
   *  their shares (optional). See calculate-capital-income.ts for the 8% / EUR 150,000 rule. */
  unlistedDividends?: number;
  unlistedNetAssets?: number;
  /** Whether to count the VAT paid when the net income is spent. Default true. When false the
   *  VAT layer is zero and netAfterVat equals the net income. */
  includeVat?: boolean;
}

export interface TaxIcebergLayer {
  key: 'employerContributions' | 'employeeTax' | 'vat' | 'netAfterVat';
  amount: number;
}

export interface TaxIcebergResult {
  grossAnnual: number;
  netAnnual: number;
  employeeTaxTotal: number;
  employerContributionsTotal: number;
  trueEmployerCost: number;
  vatPaidOnSpending: number;
  netAfterVat: number;
  /** Dividends + capital gains before tax (0 unless the person entered any). */
  capitalIncomeGross: number;
  capitalIncomeTax: number;
  /** trueEmployerCost + capitalIncomeGross: everything that flows in before any tax. */
  totalIncomeBeforeTax: number;
  /** netAfterVat / totalIncomeBeforeTax, as a percentage: what you actually keep of what you cost.
   *  With no capital income this is netAfterVat / trueEmployerCost. */
  realKeepRatePct: number;
  /** Same idea, headline framing: cost to employ you per €1 you actually get to spend. */
  costPerEuroKept: number;
  /** Bottom-to-top layers for the iceberg visualization. Amounts sum to totalIncomeBeforeTax. */
  layers: TaxIcebergLayer[];
  isGenericEstimate: boolean;
  /** The three underlying calculations, for UIs that want to show what each layer is made of. */
  /** The salary on its own: what the wage costs, what it is taxed, what is left. This is the
   *  iceberg to draw for the job; comparisons between salaries use only this part. */
  salary: {
    grossAnnual: number;
    employerContributionsTotal: number;
    trueEmployerCost: number;
    /** Wage-only contributions and taxes (the earned-income part of an unlisted dividend is
     *  attributed to `investments`, not here). */
    employeeTax: number;
    net: number;
    /** This part's share of the VAT estimate, in proportion to its share of disposable income. */
    vat: number;
    netAfterVat: number;
    keepRatePct: number;
  };
  /** Dividends and capital gains, when any were entered: a second, separate iceberg. Null
   *  otherwise. There is no employer cost on this side. */
  investments: null | {
    gross: number;
    capitalTax: number;
    /** Extra income tax caused by the earned-income part of an unlisted dividend: the salary
     *  engine's tax with that income minus without it (a marginal attribution). */
    earnedIncomeTax: number;
    tax: number;
    net: number;
    vat: number;
    netAfterVat: number;
    keepRatePct: number;
  };
  breakdown: {
    employer: EmployerCostResult;
    /** The wage on its own. */
    employee: NetSalaryResult;
    /** The wage taxed together with the earned-income part of an unlisted dividend (same as
     *  `employee` when there is none). */
    employeeWithOtherIncome: NetSalaryResult;
    vat: VatBurdenEstimate;
    capital: CapitalIncomeResult;
  };
}

export function calculateTaxIceberg(input: TaxIcebergInput): TaxIcebergResult {
  // Capital income first: the part of an unlisted dividend beyond the 8% yield is earned income
  // and has to be taxed together with the wage.
  const capital = calculateCapitalIncome({
    dividends: input.dividends,
    capitalGains: input.capitalGains,
    unlistedDividends: input.unlistedDividends,
    unlistedNetAssets: input.unlistedNetAssets,
  });
  const netSalaryInput: NetSalaryInput = {
    grossAnnual: input.grossAnnual,
    municipalTaxRatePct: input.municipalTaxRatePct,
    churchTaxRatePct: input.churchTaxRatePct,
    age: input.age,
    dependentChildren: input.dependentChildren,
    isSoleGuardian: input.isSoleGuardian,
    unionFees: input.unionFees,
    commutingCosts: input.commutingCosts,
  };
  // The wage on its own, and (only when needed) the wage taxed together with the earned-income
  // part of an unlisted dividend. The difference is the tax that dividend part causes.
  const wageOnly = calculateNetSalary(netSalaryInput);
  const netSalary =
    capital.unlistedEarnedIncomePortion > 0 ? calculateNetSalary({ ...netSalaryInput, otherEarnedIncome: capital.unlistedEarnedIncomePortion }) : wageOnly;
  const earnedIncomeTaxOnDividend = Math.max(0, netSalary.totalDeductions - wageOnly.totalDeductions);
  const employerCost = calculateEmployerCost({
    grossAnnual: netSalary.grossAnnual,
    employerSize: input.employerSize,
  });
  // VAT is paid on spending out of all disposable income, whatever its source; each part then
  // carries its share in proportion to its share of that income.
  const salaryNet = wageOnly.netAnnual;
  const investmentsNet = capital.gross - capital.tax - earnedIncomeTaxOnDividend;
  const netIncome = salaryNet + investmentsNet;
  const vat: VatBurdenEstimate =
    input.includeVat === false || !(netIncome > 0) ? { vatLiableSharePct: 0, effectiveVatRatePct: 0, vatPaid: 0 } : estimateVatBurden(netIncome);
  const salaryVat = netIncome > 0 ? vat.vatPaid * (salaryNet / netIncome) : 0;
  const investmentsVat = vat.vatPaid - salaryVat;

  const netAfterVat = netIncome - vat.vatPaid;
  const trueEmployerCost = netSalary.grossAnnual + employerCost.total;
  const totalIncomeBeforeTax = trueEmployerCost + capital.gross;
  const realKeepRatePct = totalIncomeBeforeTax > 0 ? (netAfterVat / totalIncomeBeforeTax) * 100 : 0;
  const costPerEuroKept = netAfterVat > 0 ? totalIncomeBeforeTax / netAfterVat : 0;
  const employeeTaxTotal = netSalary.totalDeductions + capital.tax;
  const salaryNetAfterVat = salaryNet - salaryVat;
  const investmentsNetAfterVat = investmentsNet - investmentsVat;

  const r = (x: number) => Math.round(x * 100) / 100;
  return {
    grossAnnual: netSalary.grossAnnual,
    netAnnual: netSalary.netAnnual,
    employeeTaxTotal: r(employeeTaxTotal),
    employerContributionsTotal: employerCost.total,
    trueEmployerCost: r(trueEmployerCost),
    vatPaidOnSpending: vat.vatPaid,
    netAfterVat: r(netAfterVat),
    capitalIncomeGross: capital.gross,
    capitalIncomeTax: capital.tax,
    totalIncomeBeforeTax: r(totalIncomeBeforeTax),
    realKeepRatePct: r(realKeepRatePct),
    costPerEuroKept: r(costPerEuroKept),
    layers: [
      { key: 'employerContributions', amount: r(employerCost.total) },
      { key: 'employeeTax', amount: r(employeeTaxTotal) },
      { key: 'vat', amount: r(vat.vatPaid) },
      { key: 'netAfterVat', amount: r(netAfterVat) },
    ],
    isGenericEstimate: netSalary.isGenericEstimate || employerCost.isGenericUnemploymentInsuranceEstimate,
    salary: {
      grossAnnual: wageOnly.grossAnnual,
      employerContributionsTotal: employerCost.total,
      trueEmployerCost: r(trueEmployerCost),
      employeeTax: wageOnly.totalDeductions,
      net: r(salaryNet),
      vat: r(salaryVat),
      netAfterVat: r(salaryNetAfterVat),
      keepRatePct: trueEmployerCost > 0 ? r((salaryNetAfterVat / trueEmployerCost) * 100) : 0,
    },
    investments:
      capital.gross > 0
        ? {
            gross: capital.gross,
            capitalTax: capital.tax,
            earnedIncomeTax: r(earnedIncomeTaxOnDividend),
            tax: r(capital.tax + earnedIncomeTaxOnDividend),
            net: r(investmentsNet),
            vat: r(investmentsVat),
            netAfterVat: r(investmentsNetAfterVat),
            keepRatePct: r((investmentsNetAfterVat / capital.gross) * 100),
          }
        : null,
    breakdown: { employer: employerCost, employee: wageOnly, employeeWithOtherIncome: netSalary, vat, capital },
  };
}
